import './MobilePreview.scss'
import 'intersection-observer'
import React, { useState } from 'react'
import { MobilePreviewProps } from './MobilePreview.types'
import IFrameResizer from 'iframe-resizer-react'
import { Loading, RenderWhenVisible } from '@marketing-milk/frontend'

export function MobilePreview({
  iframeSource,
  fluidWidth = false,
  height = 400,
  width = 325,
  frameless = false,
  fallback,
}: MobilePreviewProps) {
  // Track which src has loaded rather than a bare boolean, so a source change
  // (e.g. a refresh swapping the FB HTML) re-hides the iframe until the *new* src
  // loads instead of leaving a stale, already-loaded frame revealed.
  const [loadedSrc, setLoadedSrc] = useState<string>()
  const isLoaded = !!iframeSource && loadedSrc === iframeSource

  // Reveal-on-load: the iframe stays hidden until its `onLoad`. With a `fallback`
  // base layer underneath, "hidden iframe" means the local render shows; the iframe
  // is layered on top and revealed only once Facebook actually renders it. So if FB
  // is slow/down/timed-out, the local render simply stays visible (no pop-in).
  const hasIframe = !!iframeSource

  return (
    <RenderWhenVisible style={{ minHeight: 400 }}>
      <div
        className={frameless ? 'mx-auto' : 'border border-radius mx-auto'}
        style={{
          // With a fallback, the local card defines the height: it's taller than the
          // fixed `height` (which sizes the spinner-only case), so clamping to it would
          // spill the card over whatever sits below. The overlaid iframe is
          // absolute/`height: 100%`, so it tracks the card's natural height.
          height: fallback ? 'auto' : height,
          width: fluidWidth ? '100%' : width,
          padding: frameless ? 0 : 5,
          position: fallback ? 'relative' : undefined,
        }}
      >
        {fallback ? (
          // The fallback IS the instant base + loading state — no spinner.
          <div style={{ width: '100%' }}>{fallback}</div>
        ) : (
          !isLoaded && (
            <div style={{ width: '100%' }}>
              <Loading loadingMessage="Generating Preview" />
            </div>
          )
        )}
        {/* Don't mount an iframe for an empty src: it would load about:blank and
            fire onLoad, falsely revealing an empty overlay over the fallback. */}
        {hasIframe && (
          <IFrameResizer
            src={iframeSource}
            scrolling
            style={{
              height: 1,
              minHeight: '100%',
              width: 1,
              minWidth: '100%',
              border: 'none',
              visibility: isLoaded ? 'visible' : 'hidden',
              ...(fallback
                ? { position: 'absolute', top: 0, left: 0, height: '100%', width: '100%' }
                : {}),
            }}
            heightCalculationMethod="lowestElement"
            onLoad={() => setLoadedSrc(iframeSource)}
          />
        )}
      </div>
    </RenderWhenVisible>
  )
}
